import { useEffect, useRef, useState } from "react";
import axios from "axios";
import type {
  PlatformSettings,
  PlatformSettingsInput,
} from "@/api/platform-settings";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

type Draft = {
  version: string;
  build: string;
  url: string;
  requiredAt: string;
};
type Save = (
  input: PlatformSettingsInput,
  callbacks: {
    onSuccess: (settings: PlatformSettings) => void;
    onError: (error: Error) => void;
  }
) => void;

function draftFrom(
  settings: PlatformSettings,
  platform: "ios" | "android"
): Draft {
  const prefix = platform === "ios" ? "palIos" : "palAndroid";
  return {
    version: settings[`${prefix}MinimumVersion`] ?? "",
    build: String(settings[`${prefix}MinimumBuild`] ?? ""),
    url: settings[`${prefix}UpdateUrl`] ?? "",
    requiredAt: settings[`${prefix}UpdateRequiredAt`] ?? "",
  };
}

function validation(draft: Draft, platform: "ios" | "android"): string | null {
  if (Object.values(draft).every((value) => !value.trim())) return null;
  if (!/^\d+\.\d+\.\d+$/.test(draft.version.trim()))
    return "Enter a minimum version such as 2.3.0.";
  if (
    platform === "ios"
      ? !/^\d+(\.\d+)*$/.test(draft.build.trim())
      : !/^\d+$/.test(draft.build.trim()) ||
        !Number.isSafeInteger(Number(draft.build)) ||
        Number(draft.build) <= 0 ||
        Number(draft.build) > 2147483647
  ) {
    return platform === "ios"
      ? "Enter a numeric build, such as 12 or 12.1."
      : "Enter an Android version code from 1 to 2147483647.";
  }
  if (
    !URL.canParse(draft.url.trim()) ||
    new URL(draft.url.trim()).protocol !== "https:" ||
    new URL(draft.url.trim()).username ||
    new URL(draft.url.trim()).password
  )
    return "Enter an HTTPS update URL without a username or password.";
  // Require an explicit timezone; do not silently convert an incomplete/local date.
  if (
    draft.requiredAt.trim() &&
    (!/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}(\.\d{1,3})?(Z|[+-]\d{2}:\d{2})$/.test(
      draft.requiredAt.trim()
    ) ||
      !Number.isFinite(Date.parse(draft.requiredAt)))
  )
    return "Enter a complete ISO date with timezone, or leave it empty to warn only.";
  if (draft.requiredAt.trim()) {
    const [year, month, day] = draft.requiredAt
      .trim()
      .slice(0, 10)
      .split("-")
      .map(Number);
    if (
      month < 1 ||
      month > 12 ||
      day < 1 ||
      day > new Date(Date.UTC(year, month, 0)).getUTCDate()
    )
      return "Enter a valid calendar date.";
  }
  return null;
}

export function PalUpdatePolicyCard({
  platform,
  settings,
  save,
  pending,
  refetch,
}: {
  platform: "ios" | "android";
  settings: PlatformSettings;
  save: Save;
  pending: boolean;
  refetch: () => void;
}) {
  const title = platform === "ios" ? "PAL iOS" : "PAL Android";
  const [baseline, setBaseline] = useState(settings);
  const seenRevision = useRef(settings.updatedAt);
  const [draft, setDraft] = useState(() => draftFrom(settings, platform));
  const dirty =
    JSON.stringify(draft) !== JSON.stringify(draftFrom(baseline, platform));
  const stale = settings.updatedAt !== baseline.updatedAt;
  const [conflict, setConflict] = useState(false);
  useEffect(() => {
    if (seenRevision.current === settings.updatedAt) return;
    seenRevision.current = settings.updatedAt;
    if (!dirty) {
      setBaseline(settings);
      setDraft(draftFrom(settings, platform));
    }
  }, [settings, dirty, platform]);
  const error = validation(draft, platform);
  const off = Object.values(draft).every((value) => !value.trim());
  const fields = [
    ["version", "Minimum binary version", "2.3.0"],
    ["build", "Minimum binary build", platform === "ios" ? "12.1" : "123"],
    ["url", "Update URL", "https://…"],
    [
      "requiredAt",
      "Enforcement time (ISO with timezone)",
      "2026-11-01T12:00:00Z",
    ],
  ] as const;
  function submit() {
    if (error || !dirty || stale || conflict || pending) return;
    const version = draft.version.trim() || null;
    const url = draft.url.trim() || null;
    const requiredAt = draft.requiredAt.trim()
      ? new Date(draft.requiredAt).toISOString()
      : null;
    const input: PlatformSettingsInput =
      platform === "ios"
        ? {
            palIosMinimumVersion: version,
            palIosMinimumBuild: draft.build.trim() || null,
            palIosUpdateUrl: url,
            palIosUpdateRequiredAt: requiredAt,
          }
        : {
            palAndroidMinimumVersion: version,
            palAndroidMinimumBuild: off ? null : Number(draft.build.trim()),
            palAndroidUpdateUrl: url,
            palAndroidUpdateRequiredAt: requiredAt,
          };
    save(
      { ...input, expectedUpdatedAt: baseline.updatedAt },
      {
        onSuccess: (saved) => {
          setBaseline(saved);
          setDraft(draftFrom(saved, platform));
          setConflict(false);
        },
        onError: (failure) => {
          if (axios.isAxiosError(failure) && failure.response?.status === 409) {
            setConflict(true);
            refetch();
          }
        },
      }
    );
  }
  return (
    <Card className="max-w-2xl">
      <CardHeader>
        <CardTitle>{title} update policy</CardTitle>
        <CardDescription>
          Require an approved binary, not every newest release. Older versions
          warn before the deadline. At the deadline, drivers finish their active
          trip and sync proof, then update before the next trip. This does not
          publish a build or prove every device has updated.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <p className="text-sm">
          {off
            ? "Off — no minimum enforced"
            : draft.requiredAt.trim()
              ? "Deadline configured — older builds require an update after active work"
              : "Warn only — no enforcement time"}
        </p>
        {fields.map(([key, label, placeholder]) => (
          <div className="space-y-1.5" key={key}>
            <Label htmlFor={`${platform}-${key}`}>
              {title} {label}
            </Label>
            <Input
              id={`${platform}-${key}`}
              disabled={pending}
              value={draft[key]}
              placeholder={placeholder}
              onChange={(event) =>
                setDraft({ ...draft, [key]: event.target.value })
              }
            />
          </div>
        ))}
        <p className="text-xs text-muted-foreground">
          Leave all four fields empty to turn this platform's policy off. The
          invite install link above is separate.
        </p>
        {error && (
          <p role="alert" className="text-sm text-destructive">
            {error}
          </p>
        )}
        {(stale || conflict) && (
          <div role="alert" className="space-y-2 text-sm">
            <p>
              Settings changed since you started editing. Your draft is
              preserved. Reload the current policy before editing again.
            </p>
            <Button variant="outline" onClick={refetch}>
              Refresh current policy
            </Button>
            <Button
              variant="outline"
              disabled={pending || (conflict && !stale)}
              onClick={() => {
                setBaseline(settings);
                setDraft(draftFrom(settings, platform));
                setConflict(false);
              }}
            >
              Discard {title} draft and reload current policy
            </Button>
          </div>
        )}
        <Button
          variant="outline"
          disabled={pending || off}
          onClick={() =>
            setDraft({ version: "", build: "", url: "", requiredAt: "" })
          }
        >
          Clear {title} policy
        </Button>{" "}
        <Button
          disabled={!dirty || !!error || stale || conflict || pending}
          onClick={submit}
        >
          Save {title} policy
        </Button>
      </CardContent>
    </Card>
  );
}
